import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Schedules_Report_Monthly, Schedules_Report_Weekly, Schedules_Report_Room_Utilization } from "../api/API_Schedules";
import { Logs_Get_All, Logs_Get, Logs_Trail_Get } from "../api/API_Reports";
import { useStatus } from "./Context_Status_Global";
import { Context_UI } from "./Context_UI";
import { useMapRenderer } from "./Context_MapRenderer";
import { useProgressor } from "./Context_Progressor";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import domtoimage from "dom-to-image";


const Context_Reporting = createContext();

export default function Context_Provider_Reporting({ children }) {
    const UI = useContext(Context_UI);
    const { Status_Saving_Set, Status_Fetching_Set, Confirmer_Confirm } = useStatus();
    const { Progress_Set } = useProgressor();
    const { Map_Information, Map_Information_Type } = useMapRenderer();
    const [Output_Pages, Output_Pages_Set] = useState(0);
    const [Report_Type, Report_Type_Set] = useState("TEST");
    const [Report_Orientation, Report_Orientation_Set] = useState("Landscape");
    const [Report_Loaded, Report_Loaded_Set] = useState(false);

    const Location = useLocation();

    useEffect(() => {
        // console.error(Location.pathname);
        switch (Location.pathname) {
            case "/reports/monthlysched":
                Report_Type_Set("Schedule_Monthly");
                Report_Orientation_Set("Landscape");
                // console.warn("Report type set to Schedules_Monthly");
                break;
            case "/reports/timetable":
                Report_Type_Set("Schedule_Timetable");
                Report_Orientation_Set("Landscape");
                // console.warn("Report type set to Schedule_Timetable");
                break;
            case "/reports/floorutil":
                Report_Type_Set("Floor_Utilization");
                Report_Orientation_Set("Landscape");
                // console.warn("Report type set to Floor_Utilization");
                break;
            case "/reports/roomutil":
                Report_Type_Set("Room_Utilization");
                Report_Orientation_Set("Portrait");
                // console.warn("Report type set to Room_Utilization");
                break;
            case "/reports/reservation":
                Report_Type_Set("Reservation");
                Report_Orientation_Set("Portrait");
                // console.warn("Report type set to Reservation");
                break;
            case "/reports/accounts":
                Report_Type_Set("Accounts");
                Report_Orientation_Set("Portrait");
                // console.warn("Report type set to Accounts");
                break;
            case "/reports/usertrail":
                Report_Type_Set("User_Trail");
                Report_Orientation_Set("Landscape");
                // console.warn("Report type set to Accounts");
                break;
            case "/reports/log":
                Report_Type_Set("Logs");
                Report_Orientation_Set("Landscape");
                // console.warn("Report type set to Logs");
                break;
            default:
                Report_Type_Set("");
                // console.warn("No discernible report type");
                break;
        }
    }, [Location.pathname])

    const [Input_Schedules_Monthly, Input_Schedules_Monthly_Set] = useState({
        Month_Start: 1,
        Year_Start: 2025,
        Month_End: 12,
        Year_End: 2026,
        Include_Approved_Full: true,
        Include_Approved_Partial: false,
        Include_Pending: false,
        Include_Rejected: false,
        Include_Room: false,
        RoomID: ""
    });
    const [Output_Schedules_Monthly, Output_Schedules_Monthly_Set] = useState({
        Range_Start: new Date(),
        Range_End: new Date(),
        Schedules: []
    });

    const [Input_Schedules_Timetable, Input_Schedules_Timetable_Set] = useState({
        Week_Start: 1,
        Year_Start: 2025,
        Week_End: 53,
        Year_End: 2026,
        RoomID: ""
    });

    const [Output_Schedules_Timetable, Output_Schedules_Timetable_Set] = useState({
        Range_Start: new Date(),
        Range_End: new Date(),
        RoomID: "",
        Schedules: []
    });

    const Report_Schedules_Monthly = async () => {
        // Report_Type_Set("Schedules_Monthly");
        Report_Loaded_Set(false);
        Status_Fetching_Set("InProgress");
        try {
            const Request = {
                Year_Start: Number(Input_Schedules_Monthly.Year_Start),
                Month_Start: Number(Input_Schedules_Monthly.Month_Start),
                Year_End: Number(Input_Schedules_Monthly.Year_End),
                Month_End: Number(Input_Schedules_Monthly.Month_End),
                Include_Approved_Full: Input_Schedules_Monthly.Include_Approved_Full,
                Include_Approved_Partial: Input_Schedules_Monthly.Include_Approved_Partial,
                Include_Pending: Input_Schedules_Monthly.Include_Pending,
                Include_Rejected: Input_Schedules_Monthly.Include_Rejected,
                Include_Room: Input_Schedules_Monthly.Include_Room,
                RoomID: Input_Schedules_Monthly.RoomID
            };
            // console.error(Request);
            const Data_Raw = await Schedules_Report_Monthly(Request);
            const Data = Rearrange_Schedules_Monthly(Data_Raw.data);
            Output_Schedules_Monthly_Set(Data);
            Status_Fetching_Set("Success");
            Report_Loaded_Set(true);
        } catch (Error) {
            Status_Fetching_Set("Error");
            // console.error(Error);
        }
    }

    const Rearrange_Schedules_Monthly = (Data_Raw) => {
        const { Range_Start, Range_End, Data } = Data_Raw;

        const Month_Get = (dateString) => {
            const d = new Date(dateString);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        };

        const grouped = {};
        Data.forEach(schedule => {
            const key = Month_Get(schedule.Time.Start);
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(schedule);
        });

        const groupedArray = Object.keys(grouped).map(key => {
            const [year, month] = key.split("-");
            const monthDate = new Date(Number(year), Number(month) - 1, 1);
            return {
                Month: monthDate,
                Schedules: grouped[key]
            };
        });

        groupedArray.sort((a, b) => a.Month - b.Month);

        return {
            Range_Start,
            Range_End,
            Data: groupedArray
        };
    }

    const Report_Schedules_Timetable = async () => {
        Report_Loaded_Set(false);
        Status_Fetching_Set("InProgress");
        try {
            const Request = {
                Year_Start: Number(Input_Schedules_Timetable.Year_Start),
                Week_Start: Number(Input_Schedules_Timetable.Week_Start), Year_End: Number(Input_Schedules_Timetable.Year_End),
                Week_End: Number(Input_Schedules_Timetable.Week_End),
                RoomID: Input_Schedules_Timetable.RoomID
            };
            // console.error(Request);
            const Data_Raw = await Schedules_Report_Weekly(Request);
            const Data = Rearrange_Schedules_Timetable(Data_Raw.data.Data);
            // console.warn(Data_Raw.data);
            // console.warn(Data);
            Output_Schedules_Timetable_Set({
                Range_Start: Data_Raw.data.Range_Start,
                Range_End: Data_Raw.data.Range_End,
                RoomID: Data_Raw.data.RoomID,
                Schedules: Data
            })
            Status_Fetching_Set("Success");
            Report_Loaded_Set(true);
        } catch (Error) {
            Status_Fetching_Set("Error");
            console.error(Error);
        }
    };

    const Rearrange_Schedules_Timetable = (Data_Raw) => {
        const Date_Week_Number_Get = (date) => {
            const Target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const Day_Number = Target.getUTCDay() || 7;
            Target.setUTCDate(Target.getUTCDate() + 4 - Day_Number);
            const Year_Start = new Date(Date.UTC(Target.getUTCFullYear(), 0, 1));
            const Week_Number = Math.ceil((((Target - Year_Start) / 86400000) + 1) / 7);
            return {
                Year: Target.getUTCFullYear(),
                Week: Week_Number
            };
        }

        const Grouped = {};
        Data_Raw.forEach(schedule => {
            const { Year, Week } = Date_Week_Number_Get(new Date(schedule.Time.Start));
            const Key = `${Year}-W${Week}`;
            if (!Grouped[Key]) {
                Grouped[Key] = {
                    Year,
                    Week,
                    Schedules: []
                }
            }
            Grouped[Key].Schedules.push(schedule);
        });

        return Object.values(Grouped).sort((a, b) => {
            if (a.Year !== b.Year) {
                return a.Year - b.Year;
            }
            return a.Week - b.Week;
        })
    }

    const [Input_Room_Utilization, Input_Room_Utilization_Set] = useState({
        RoomID: "689d4d23f97b971a7615ca15",
        Filter_Date_Start: new Date(2025, 0, 1),
        Filter_Date_End: new Date()
    });

    const [Output_Room_Utilization, Output_Room_Utilization_Set] = useState({
        RoomID: "",
        Count_Reservations_Total: 0,
        Count_Reservations_Hours: 0,
        Count_Reservations_Hours_Total: 0,
        Count_Utilization_Rate: 0,
        Count_Reservations_Approved: 0,
        Count_Reservations_Rejected: 0,
        Count_Reservations_Pending: 0,
        Count_Reservations_Rescheduled: 0,
        Count_Reservations_Relocated: 0,
        Commonality_Reservations_Rejected: [],
        Commonality_Reservations_Rescheduled: [],
        Commonality_Reservations_Relocated: [],
        Commonality_Activity_Type: [],
        Commonality_Requesters: [],
        Commonality_Requester_Type: [],
        Commonality_Equipment: [],
        Commonality_Reservations_Hours_Start: new Date(),
        Commonality_Reservations_Hours_End: new Date()
    });

    useEffect(() => {
        if (Map_Information_Type === "Room" && Map_Information) {
            Input_Room_Utilization_Set({
                RoomID: Map_Information._id
            })
        }
    }, [Map_Information, Map_Information_Type]);


    const Report_Room_Utilization = async () => {
        Report_Loaded_Set(false);
        Status_Fetching_Set("InProgress");
        try {
            const Request = Input_Room_Utilization;
            const Data_Raw = await Schedules_Report_Room_Utilization(Request);
            // console.warn(Data_Raw.data);
            Output_Room_Utilization_Set(Data_Raw.data);
            Report_Loaded_Set(true);
            Status_Fetching_Set("Success");
        } catch (Error) {
            Status_Fetching_Set("Error");
            console.error(Error);
        }
    }
    const [Input_Logs, Input_Logs_Set] = useState({
        Search_Type: "General",
        Filter_SearchTerm: "",
        Filter_Date_Start: new Date(2025, 0, 1),
        Filter_Date_End: new Date(),
        Filter_Action: "",
        Filter_Document_Type: "",
        Filter_Document_ID: ""
    });
    const [Output_Logs, Output_Logs_Set] = useState([]);
    const Logs_Types = [
        {
            Action: "ALL",
            Title: "View all",
            Description: "View all",
            Prefix: "AM"
        },
        {
            Action: "AM_Register",
            Title: "Account registered",
            Description: "Creation of account by a user",
            Prefix: "AM"
        },
        {
            Action: "AM_Verify",
            Title: "Account verified",
            Description: "The user has successfully verified their account",
            Prefix: "AM"
        },
        {
            Action: "AM_Reverify",
            Title: "Account reverified",
            Description: "The user requested a new verification link from the system",
            Prefix: "AM"
        },
        {
            Action: "AM_Update",
            Title: "Account updated",
            Description: "The user had updated an account's details",
            Prefix: "AM"
        },
        {
            Action: "AM_Delete",
            Title: "Account deleted",
            Description: "The user had deleted an account from the database (only happens to rejected accounts)",
            Prefix: "AM"
        },
        {
            Action: "SM_Reservation_Add",
            Title: "Reservation added",
            Description: "The user had created a reservation",
            Prefix: "SM"
        },
        {
            Action: "SM_Reservation_Approve_Initial",
            Title: "Reservation approved by OSA",
            Description: "The user had approved a reservation in the OSA side",
            Prefix: "SM"
        },
        {
            Action: "SM_Reservation_Approve_Final",
            Title: "Reservation approved by GSU",
            Description: "The user had approved a reservation in the GSU side",
            Prefix: "SM"
        },
        {
            Action: "SM_Reservation_Reject_Initial",
            Title: "Reservation rejected by OSA",
            Description: "The user had rejected a reservation in the OSA side",
            Prefix: "SM"
        },
        {
            Action: "SM_Reservation_Reject_Final",
            Title: "Reservation rejected by GSU",
            Description: "The user had rejected a reservation in the GSU side",
            Prefix: "SM"
        },
        {
            Action: "SM_Reservation_Reschedule",
            Title: "Reservation rescheduled",
            Description: "The user had rescheduled a reservation",
            Prefix: "SM"
        },
        {
            Action: "SM_Reservation_Relocate",
            Title: "Reservation relocated",
            Description: "The user had relocated a reservation to another room",
            Prefix: "SM"
        },
        {
            Action: "SM_Reservation_Edit",
            Title: "Reservation details edited",
            Description: "The user had edited the details of a reservation",
            Prefix: "SM"
        }
    ];
    const Logs_Categories = [
        {
            Category: "All",
            Prefix: "ALL"
        },
        {
            Category: "Accounts Management",
            Prefix: "AM"
        },
        {
            Category: "Schedules Management",
            Prefix: "SM"
        }
    ];
    const Logs_Types_Documents = [
        {
            Type: "Accounts"
        },
        {
            Type: "Schedule"
        }
    ];
    const Report_Logs = async () => {
        // console.warn(Input_Logs);
        Report_Loaded_Set(false);
        Status_Fetching_Set("InProgress");
        try {
            if (Input_Logs.Search_Type !== "All") {
                const Data_Raw = await Logs_Get(Input_Logs);
                // console.warn(Data_Raw.data);
                Output_Logs_Set(Data_Raw.data);
                Report_Loaded_Set(true);
                Status_Fetching_Set("Success");
            } else {
                const Data_Raw = await Logs_Get_All();
                // console.warn(Data_Raw.data);
                Output_Logs_Set(Data_Raw.data);
                Report_Loaded_Set(true);
                Status_Fetching_Set("Success");
            }
            
        } catch (Error) {
            Status_Fetching_Set("Error");
            console.error(Error);
        }
    }

    const [Input_Logs_Trail, Input_Logs_Trail_Set] = useState({
        ID: "",
        Filter_Date_Start: new Date(2025, 0, 1),
        Filter_Date_End: new Date()
    });
    const [Output_Logs_Trail, Output_Logs_Trail_Set] = useState([]);

    const Report_Logs_Trail = async () => {
        Report_Loaded_Set(false);
        Status_Fetching_Set("InProgress");
        try {
            const Data_Raw = await Logs_Trail_Get(Input_Logs_Trail);
            Output_Logs_Trail_Set(Data_Raw.data);
            Report_Loaded_Set(true);
            Status_Fetching_Set("Success");
        } catch (Error) {
            Status_Fetching_Set("Error");
            console.error(Error);
        }
    }


    useEffect(() => {
        Report_Loaded_Set(false);
    }, []);


    const [Report_Presentation, Report_Presentation_Set] = useState("Normal");

    const [Report_Output_Type, Report_Output_Type_Set] = useState("");

    const [Report_Output_Scale, Report_Output_Scale_Set] = useState(1.5);

    const [Report_Output_Progress, Report_Output_Progress_Set] = useState({
        State: "Ready",
        Count_Total: 0,
        Count_Current: 0,
        Percent_Current: 0.0
    });


    const Export_AsPDF = async () => {
        Report_Output_Type_Set("PDF");
        Report_Presentation_Set("Document");
        const { Response, Data } = await Confirmer_Confirm('Window_Report_Export');
        if (Response === "Confirmed") {
            const Scale = Data.FileScale;
            console.warn(Report_Output_Scale);
            console.warn(Data.FileScale);
            UI.Subwindows_Open("Window_Report_Export_Processing");
            const Pages = document.querySelectorAll(".Document_Paper").length;
            Progress_Set(Previous => ({
                ...Previous,
                State: "Processing",
                Count_Total: Pages,
                Count_Current: 0,
                Percent_Current: 0.0
            }));
            // Output_Pages_Set(Pages);

            window.Toasts_CreateToast(
                "https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png",
                "Exporting document into PDF",
                "The download will be available shortly."
            );

            const Orientation = document.querySelectorAll(".Document_Paper")[0].getAttribute("Orientation");
            const Orientation_Lowercase = Orientation === "Portrait" ? "portrait" : "landscape";
            const PDF = new jsPDF({
                orientation: Orientation_Lowercase,
                unit: "in",
                format: "letter",
            });

            const PageWidth = Orientation_Lowercase === "landscape" ? 11 : 8.5;
            const PageHeight = Orientation_Lowercase === "landscape" ? 8.5 : 11;

            console.warn(`Output is ${Orientation_Lowercase}, Page: ${PageWidth}x${PageHeight} inches`);

            for (let a = 1; a <= Pages; a++) {
                const Element = document.getElementById(`Document_Paper_${a}`);
                if (!Element) {
                    console.error("Error exporting document.");
                    return;
                }

                const Rectangle = Element.getBoundingClientRect();

                // upscale render
                const Style = {
                    transform: `scale(${Scale})`,
                    transformOrigin: "top left",
                    width: `${Rectangle.width}px`,
                    height: `${Rectangle.height}px`,
                };

                const Render = {
                    width: Rectangle.width * Scale,
                    height: Rectangle.height * Scale,
                    style: Style,
                    cacheBust: true,
                    bgcolor: "#ffffff",
                };

                try {
                    const Output = await domtoimage.toPng(Element, Render);
                    if (a !== 1) PDF.addPage();
                    PDF.addImage(Output, "PNG", 0, 0, PageWidth, PageHeight);
                    Progress_Set(Previous => ({
                        ...Previous,
                        State: "Processing",
                        Count_Total: Pages,
                        Count_Current: a,
                        Percent_Current: (a/Pages) * 100
                    }));
                } catch (Error) {
                    // UI.Subwindows_Close("Window_Report_Export_Processing");
                    window.Toasts_CreateToast(
                        "https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png",
                        "An error occured.",
                        "Please try again."
                    );
                    Progress_Set(Previous => ({
                        ...Previous,
                        State: "Error",
                        Count_Total: Pages,
                        Count_Current: a,
                        Percent_Current: (a / Pages) * 100
                    }));
                    console.error("Export failed:", Error);
                }
            }
            // UI.Subwindows_Close("Window_Report_Export_Processing");
            Progress_Set(Previous => ({
                ...Previous,
                State: "Success",
                Count_Total: Pages,
                Count_Current: Pages,
                Percent_Current: 100
            }));
            PDF.save(`${Data.FileName}.pdf`);
            window.Toasts_CreateToast(
                "https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png",
                "Document exported into PDF successfully.",
                ""
            );
        }
        
    };


    const Export_AsImage = async () => {
        Report_Output_Type_Set("Image");
        Report_Presentation_Set("Document");
        const { Response, Data } = await Confirmer_Confirm('Window_Report_Export');
        if (Response === "Confirmed") {
            UI.Subwindows_Open("Window_Report_Export_Processing");
            const Scale = Data.FileScale;
            // Output_Pages_Set(document.querySelectorAll(".Document_Paper").length);
            const Pages = document.querySelectorAll(".Document_Paper").length;
            window.Toasts_CreateToast("https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png", "Exporting document into image(s)", "The download(s) will be available shortly.");

            Progress_Set(Previous => ({
                ...Previous,
                State: "Processing",
                Count_Total: Pages,
                Count_Current: 0,
                Percent_Current: 0.0
            }));

            for (let a = 1; a < Pages + 1; a++) {
                const Element = document.getElementById(`Document_Paper_${a}`);
                if (!Element) {
                    console.error("Error exporting document.");
                    return;
                }
                const Rectangle = Element.getBoundingClientRect();
                const Style = {
                    transform: `scale(${Scale})`,
                    transformOrigin: "top left",
                    width: `${Rectangle.width}px`,
                    height: `${Rectangle.height}px`
                };

                const Render = {
                    width: Rectangle.width * Scale,
                    height: Rectangle.height * Scale,
                    style: Style,
                    cacheBust: true,
                    bgcolor: "#ffffff"
                }

                try {
                    const Output = await domtoimage.toPng(Element, Render);
                    Progress_Set(Previous => ({
                        ...Previous,
                        State: "Processing",
                        Count_Total: Pages,
                        Count_Current: a,
                        Percent_Current: (a / Pages) * 100
                    }));
                    const Link = document.createElement("a");
                    Link.href = Output;
                    Link.download = `${Data.FileName}_${a}`;
                    document.body.appendChild(Link);
                    Link.click();
                    document.body.removeChild(Link);
                } catch (Error) {
                    console.error(Error);
                    window.Toasts_CreateToast(
                        "https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png",
                        "An error occured.",
                        "Please try again."
                    );
                    Progress_Set(Previous => ({
                        ...Previous,
                        State: "Error",
                        Count_Total: Pages,
                        Count_Current: a,
                        Percent_Current: (a / Pages) * 100
                    }));
                    // UI.Subwindows_Close("Window_Report_Export_Processing");
                }
                /*const Canvas = await html2canvas(Element, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
                const Image = Canvas.toDataURL("image/png");
                const Link = document.createElement("a");
                Link.href = Image;
                Link.download = `Document_Page_${a}`;
                document.body.appendChild(Link);
                Link.click();
                document.body.removeChild(Link);*/
            }
            // UI.Subwindows_Close("Window_Report_Export_Processing");
            Progress_Set(Previous => ({
                ...Previous,
                State: "Success",
                Count_Total: Pages,
                Count_Current: Pages,
                Percent_Current: 100
            }));
        }
        
    }

    const Export_AsPrintable = async () => {
        try {
            const Scale = Report_Output_Scale;
            Output_Pages_Set(document.querySelectorAll(".Document_Paper").length);
            const Pages = document.querySelectorAll(".Document_Paper").length
            console.error(Pages);
            window.Toasts_CreateToast("https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png", "Preparing for print...", "The printable will be available shortly.");
            const Orientation = document.querySelectorAll(".Document_Paper")[0].getAttribute("Orientation");

            const Orientation_Lowercase = Orientation === "Portrait" ? "portrait" : "landscape";
            const PDF = new jsPDF({
                orientation: Orientation_Lowercase,
                unit: "in",
                format: "letter",
            });

            const PageWidth = Orientation_Lowercase === "landscape" ? 11 : 8.5;
            const PageHeight = Orientation_Lowercase === "landscape" ? 8.5 : 11;

            // const PDF = new jsPDF({ orientation: "landscape", unit: "in", format: "letter" });

            for (let a = 1; a < Pages + 1; a++) {
                const Element = document.getElementById(`Document_Paper_${a}`);
                if (!Element) {
                    console.error("Error exporting document.");
                    return;
                }
                /*const Canvas = await html2canvas(Element, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
                const Image = Canvas.toDataURL("image/png");*/
                const Rectangle = Element.getBoundingClientRect();
                const Style = {
                    transform: `scale(${Scale})`,
                    transformOrigin: "top left",
                    width: `${Rectangle.width}px`,
                    height: `${Rectangle.height}px`
                };

                const Render = {
                    width: Rectangle.width * Scale,
                    height: Rectangle.height * Scale,
                    style: Style,
                    cacheBust: true,
                    bgcolor: "#ffffff"
                }

                try {
                    const Output = await domtoimage.toPng(Element, Render);
                    if (a !== 1) {
                        PDF.addPage();
                    }
                    PDF.addImage(Output, "PNG", 0, 0, PageWidth, PageHeight);
                } catch (Error) {
                    console.error(Error);
                }
            }

            const PDF_Blob = PDF.output("blob");
            const PDF_URL = URL.createObjectURL(PDF_Blob);

            const Window = window.open(PDF_URL);
            if (Window) {
                Window.onload = () => {
                    setTimeout(() => {
                        Window.focus();
                        Window.print();
                    }, 500);
                }
            } else {
                window.alert("Please allow popups and try again.");
            }       
        } catch (Error) {
            window.Toasts_CreateToast(
                "https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png",
                "An error occured.",
                "Please try again."
            );
        }
       
    }

    const Export_Map = async () => {
        Output_Pages_Set(document.querySelectorAll(".Document_Paper").length);
        const Pages = document.querySelectorAll(".Document_Paper").length;
        window.Toasts_CreateToast("https://bhimlex13.github.io/Student-Assistant-Portal/Assets/Icons/Logo.png", "Exporting document into image(s)", "The download(s) will be available shortly.");
        const Scale = 3;
        const Width = 11;
        const Height = 8.5;
        const PDF = new jsPDF({ orientation: "landscape", unit: "in", format: "letter" });
        for (let a = 1; a < Pages + 1; a++) {
            const Element = document.getElementById(`Document_Paper_${a}`);
            if (!Element) {
                console.error("Error exporting document.");
                return;
            }
            const Rectangle = Element.getBoundingClientRect();
            const Style = {
                transform: `scale(${Scale})`,
                transformOrigin: "top left",
                width: `${Rectangle.width}px`,
                height: `${Rectangle.height}px`
            };

            const Render = {
                width: Rectangle.width * Scale,
                height: Rectangle.height * Scale,
                style: Style,
                cacheBust: true,
                bgcolor: "#ffffff"
            }

            try {
                const Output = await domtoimage.toPng(Element, Render);
                const Link = document.createElement("a");
                Link.href = Output;
                Link.download = `Document_Page_${a}`;
                document.body.appendChild(Link);
                Link.click();
                document.body.removeChild(Link);

                if (a !== 1) {
                    PDF.addPage();
                }
                PDF.addImage(Output, "PNG", 0, 0, Width, Height); 
            } catch (Error) {
                console.error(Error);
            }            
        }
        PDF.save("TEST.pdf");
    }
    return (
        <Context_Reporting.Provider value={{
            Report_Type,
            Report_Type_Set,
            Report_Orientation,
            Report_Schedules_Monthly,
            Output_Schedules_Monthly,
            Report_Loaded,
            Export_AsPDF,
            Export_AsImage,
            Export_AsPrintable,
            Output_Pages,
            Output_Pages_Set,
            Input_Schedules_Monthly,
            Input_Schedules_Monthly_Set,
            Input_Schedules_Timetable,
            Input_Schedules_Timetable_Set,
            Report_Schedules_Timetable,
            Output_Schedules_Timetable,
            Export_Map,
            Input_Room_Utilization,
            Input_Room_Utilization_Set,
            Report_Room_Utilization,
            Output_Room_Utilization,
            Report_Logs,
            Output_Logs,
            Logs_Types,
            Logs_Categories,
            Logs_Types_Documents,
            Input_Logs,
            Input_Logs_Set,
            Report_Presentation,
            Report_Presentation_Set,
            Report_Output_Type,
            Report_Logs_Trail,
            Input_Logs_Trail,
            Input_Logs_Trail_Set,
            Output_Logs_Trail,
            Output_Logs_Trail_Set,
            Report_Output_Progress,
            Report_Output_Scale,
            Report_Output_Scale_Set
        }}>
            {children}
        </Context_Reporting.Provider>
    )
}

export function useReporting() {
    return useContext(Context_Reporting);
}